import type { ElplanInfoTagForDate } from 'api/elplan/elplan.types'
import { getBidTagsInBidInputOrder } from 'helpers/elplan.helper/bidTagOrder.helper'
import i18n from 'i18next'
import Datetime from 'utils/datetime/datetime'
import type { DayAheadBidColumn, DayAheadBidProductionConsumption, DayAheadBidRow } from './DayAheadBidTable.types'

export function buildDayAheadBidColumns(
  tags: ElplanInfoTagForDate[],
  productionConsumption: DayAheadBidProductionConsumption
): DayAheadBidColumn[] {
  return getBidTagsInBidInputOrder(tags, productionConsumption).map((tag) => ({
    tagId: tag.id,
    tagKey: tag.tag_key,
    buySell: tag.buy_sell,
  }))
}

export function buildDayAheadBidRows(startTime: ISODateTime, endTime: ISODateTime): DayAheadBidRow[] {
  const hourStarts = Datetime.getHoursBetween(startTime, endTime)

  return [
    { kind: 'name' },
    { kind: 'price' },
    ...hourStarts.map((hourStart) => ({
      kind: 'hour' as const,
      startTime: hourStart,
      endTime: Datetime.addHours(hourStart, 1),
    })),
  ]
}

export function makeDayAheadBidHourRowLookup(rows: DayAheadBidRow[]): (instant: ISODateTime) => number | undefined {
  const rowIndexByHourStart = new Map<ISODateTime, number>(
    rows.flatMap((row, rowIndex) => (row.kind === 'hour' ? [[Datetime.toISOString(row.startTime), rowIndex]] : []))
  )

  return (instant) => rowIndexByHourStart.get(Datetime.toISOString(instant))
}

type BuySellGroup = {
  buySell: DayAheadBidColumn['buySell']
  columns: DayAheadBidColumn[]
}

type BuySellGroupedColumn = {
  column: DayAheadBidColumn
  isGroupStart: boolean
  isGroupEnd: boolean
}

export function getDayAheadBidBuySellGroups(columns: DayAheadBidColumn[]): {
  groups: BuySellGroup[]
  columns: BuySellGroupedColumn[]
} {
  const groups = columns.reduce<BuySellGroup[]>((grouped, column) => {
    const openGroup = grouped.at(-1)
    if (openGroup?.buySell === column.buySell) {
      openGroup.columns.push(column)
      return grouped
    }

    return [...grouped, { buySell: column.buySell, columns: [column] }]
  }, [])

  return {
    groups,
    columns: groups.flatMap((group) =>
      group.columns.map((column, indexInGroup) => ({
        column,
        isGroupStart: indexInGroup === 0,
        isGroupEnd: indexInGroup === group.columns.length - 1,
      }))
    ),
  }
}

export function isSingleDayAheadBidDay(startTime: ISODateTime, endTime: ISODateTime): boolean {
  return Datetime.isSameDate(startTime, endTime)
}

function getHourRangeLabel(row: Extract<DayAheadBidRow, { kind: 'hour' }>): string {
  const offsetMinutes = Datetime.getLocalUtcOffsetMinutes(row.startTime)

  return `${Datetime.toHourAtUtcOffset(row.startTime, offsetMinutes)} - ${Datetime.toHourAtUtcOffset(row.endTime, offsetMinutes)}`
}

export function getDayAheadBidRowLabels(rows: DayAheadBidRow[], currency: string): string[] {
  const hourRanges = rows.map((row) => (row.kind === 'hour' ? getHourRangeLabel(row) : null))
  const occurrences = new Map<string, number>()
  for (const range of hourRanges) {
    if (range !== null) {
      occurrences.set(range, (occurrences.get(range) ?? 0) + 1)
    }
  }

  return rows.map((row, index) => {
    const range = hourRanges[index]
    if (row.kind === 'name') {
      return i18n.t('Name', { defaultValue: 'Name' }) || 'Name'
    }

    if (row.kind !== 'hour' || range === null) {
      return `${i18n.t('Avg price')} (${currency}/MWh)`
    }

    return (occurrences.get(range) ?? 0) > 1 ? `${range} (${Datetime.getLocalUtcOffset(row.startTime)})` : range
  })
}

const CELL_KEY_PATTERN = /^(\d+)::(\d+)$/

export function getDayAheadBidCellKey(rowIndex: number, tagId: number): string {
  return `${rowIndex}::${tagId}`
}

export function parseDayAheadBidCellKey(cellKey: string): { rowIndex: number; tagId: number } | null {
  const match = CELL_KEY_PATTERN.exec(cellKey)

  return match ? { rowIndex: Number(match[1]), tagId: Number(match[2]) } : null
}
